'use client'

import { Star, StarHalf, ThumbsUp, ThumbsDown } from 'lucide-react'
import { useDashboardTranslation } from '@/hooks/useDashboardTranslation'
import { usePropertyRole } from '@/hooks/usePropertyRole'
import { useCapabilitySet } from '@/hooks/useCapability'
import { decideCapability } from '@/lib/capabilities/gating'

// ── Channel Constants ──────────────────────────────────────────────

export const CHANNEL_COLORS: Record<string, string> = {
  BDC: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  ABB: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  EXP: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
  AGO: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
}

export const CHANNEL_NAMES: Record<string, string> = {
  BDC: 'Booking.com',
  ABB: 'Airbnb',
  EXP: 'Expedia',
  AGO: 'Agoda',
}

// ── Reply Authorization ────────────────────────────────────────────

/**
 * Roles holding UPDATE on `public.channex_reviews` (reply_text / replied_at / replied_by).
 *
 * This IS the `update_permissions` role list in
 * `taskyinn-backend/hasura/metadata/databases/default/tables/public_channex_reviews.yaml` —
 * change them together. accountant and property_admin read reviews but may not reply.
 *
 * Gate on the role the REQUEST runs as (`usePropertyRole().roleKey`, which folds in the
 * per-tab role switcher), never `x-hasura-default-role`: a tenant_admin acting as front_desk
 * carries the admin default role, so the token claim showed the reply form to a session
 * whose mutations Hasura then rejected.
 */
export const REVIEW_REPLY_ROLES = new Set(['front_desk', 'manager', 'owner', 'tenant_admin'])

/**
 * May the current session use the review-reply flow? (#2324)
 *
 * With capability gating ON (and no bypass role) the `reviews.reply`
 * special-op key decides — seeded to exactly REVIEW_REPLY_ROLES' non-bypass
 * half (route-grants.ts), so flipping the flag is behavior-neutral. Flag off
 * (or bypass) keeps today's role-list gate above, unchanged. Same
 * flag-fallback idiom as useCanApprove in tasks/approval-gate.tsx.
 */
export function useCanReplyToReview(): boolean {
  const { roleKey } = usePropertyRole()
  const { gatingEnabled, bypass, capabilitySet } = useCapabilitySet()
  if (gatingEnabled && !bypass) {
    return decideCapability('reviews.reply', { gatingEnabled, bypass, capabilitySet })
  }
  // roleKey null = platform owner / no membership row — today's behavior.
  return roleKey === null || REVIEW_REPLY_ROLES.has(roleKey)
}

// ── Rating Stars ───────────────────────────────────────────────────

export function RatingStars({ rating }: { rating: number }) {
  const fullStars = Math.floor(rating)
  const hasHalfStar = rating % 1 >= 0.5

  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => {
        if (i < fullStars) {
          return <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
        } else if (i === fullStars && hasHalfStar) {
          return <StarHalf key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
        } else {
          return <Star key={i} className="h-4 w-4 text-gray-300" />
        }
      })}
      <span className="ml-1.5 text-sm font-medium">{rating.toFixed(1)}</span>
    </div>
  )
}

// ── Structured Review Text ─────────────────────────────────────────

interface ParsedReview {
  headline?: string
  positive?: string
  negative?: string
  publicReview?: string
  privateFeedback?: string
}

/** Parse OTA structured review text into sections */
export function parseReviewText(text: string): ParsedReview | null {
  // Booking.com format: "Headline: ...\r\n\r\nPositive: ...\r\n\r\nNegative: ..."
  if (text.includes('Positive:') || text.includes('Negative:')) {
    const sections: ParsedReview = {}

    const headlineMatch = text.match(/^Headline:\s*(.*?)(?=\r?\n|\s*Positive:|\s*Negative:|$)/s)
    if (headlineMatch?.[1]?.trim()) sections.headline = headlineMatch[1].trim()

    const positiveMatch = text.match(/Positive:\s*(.*?)(?=\r?\n\r?\n\s*Negative:|$)/s)
    if (positiveMatch?.[1]?.trim()) sections.positive = positiveMatch[1].trim()

    const negativeMatch = text.match(/Negative:\s*(.*?)$/s)
    if (negativeMatch?.[1]?.trim()) sections.negative = negativeMatch[1].trim()

    if (sections.headline || sections.positive || sections.negative) return sections
  }

  // Airbnb format: "Public review:\r\n...\r\n\r\nPrivate feedback:\r\n..."
  if (text.includes('Public review:')) {
    const sections: ParsedReview = {}

    const publicMatch = text.match(/Public review:\s*\r?\n?(.*?)(?=\r?\n\r?\n\s*Private feedback:|$)/s)
    if (publicMatch?.[1]?.trim()) sections.publicReview = publicMatch[1].trim()

    const privateMatch = text.match(/Private feedback:\s*\r?\n?(.*?)$/s)
    if (privateMatch?.[1]?.trim()) sections.privateFeedback = privateMatch[1].trim()

    if (sections.publicReview || sections.privateFeedback) return sections
  }

  return null
}

export function StructuredReviewText({ text }: { text: string }) {
  const { td } = useDashboardTranslation()
  const parsed = parseReviewText(text)

  if (!parsed) {
    return (
      <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
        {text}
      </p>
    )
  }

  return (
    <div className="space-y-2.5">
      {/* BDC sections */}
      {parsed.headline && (
        <p className="text-sm font-medium">{parsed.headline}</p>
      )}
      {parsed.positive && (
        <div className="flex gap-2">
          <ThumbsUp className="h-3.5 w-3.5 mt-0.5 shrink-0 text-green-600 dark:text-green-400" />
          <p className="text-sm text-muted-foreground leading-relaxed">{parsed.positive}</p>
        </div>
      )}
      {parsed.negative && (
        <div className="flex gap-2">
          <ThumbsDown className="h-3.5 w-3.5 mt-0.5 shrink-0 text-red-500 dark:text-red-400" />
          <p className="text-sm text-muted-foreground leading-relaxed">{parsed.negative}</p>
        </div>
      )}
      {/* Airbnb sections */}
      {parsed.publicReview && (
        <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
          {parsed.publicReview}
        </p>
      )}
      {parsed.privateFeedback && (
        <div className="rounded-md bg-muted/50 border border-dashed p-2.5">
          <p className="text-xs font-medium text-muted-foreground mb-1">{td('reviews.privateFeedback')}</p>
          <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
            {parsed.privateFeedback}
          </p>
        </div>
      )}
    </div>
  )
}
